import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import { Divider, Grid, List, ListItem, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { ExtraActionsMenu } from 'components';
import { ConfirmationDialog } from 'components/Dialogs';
import { Action } from 'components/Dialogs/common/Dialogs.props';
import { InformationDashboard409AContext } from 'context/InformationDashboard409AContext';
import {
  ERROR_DELETING_REQUESTED_QUESTION,
  SUCCESS_DELETING_REQUESTED_QUESTION,
  SUCCESS_MODIFYING_INFORMATION_REQUEST,
} from 'dashboard409a/common/constants';
import { modifyExistingRequest } from 'dashboard409a/services/dashboard';
import { ModalAnswerRow } from 'pages/Funds/components';
import theme from 'theme';
import { CurrentRequestedQuestionsListProps, QuestionProgress } from './types';

const CurrentRequestedQuestionsList: FC<CurrentRequestedQuestionsListProps> = ({ questions }) => {
  const lastTaskId = questions?.id || null;
  const [isOpenDeleteQuestionRequestModal, setIsOpenDeleteQuestionRequestModal] = useState(false);
  const [questionToRemove, setQuestionToRemove] = useState<QuestionProgress | null>(null);
  const { handleGetCurrentInformationRequests, permissions } = useContext(InformationDashboard409AContext);
  const { enqueueSnackbar } = useSnackbar();

  const openQuestionRequestModal = useCallback((document: QuestionProgress) => {
    setQuestionToRemove(document);
    setIsOpenDeleteQuestionRequestModal(true);
  }, []);

  const removeQuestion = useCallback(
    async (question: QuestionProgress) => {
      try {
        if (!lastTaskId || !question.id) {
          enqueueSnackbar(ERROR_DELETING_REQUESTED_QUESTION, { variant: 'error' });
          return false;
        }
        const data = {
          task: lastTaskId,
          questions_to_delete: [question.id],
          requested_documents: [],
        };
        const response = await modifyExistingRequest(data);

        if (response?.message === SUCCESS_MODIFYING_INFORMATION_REQUEST) {
          enqueueSnackbar(SUCCESS_DELETING_REQUESTED_QUESTION, { variant: 'success' });
          return true;
        }
        enqueueSnackbar(ERROR_DELETING_REQUESTED_QUESTION, { variant: 'error' });
        return false;
      } catch (error) {
        enqueueSnackbar(ERROR_DELETING_REQUESTED_QUESTION, { variant: 'error' });
        return false;
      } finally {
        setQuestionToRemove(null);
        setIsOpenDeleteQuestionRequestModal(false);
        handleGetCurrentInformationRequests();
      }
    },
    [lastTaskId, enqueueSnackbar, handleGetCurrentInformationRequests]
  );

  const optionsForExtraActionsMenu = useCallback(
    (questionToRemoveParam: QuestionProgress | null) => [
      {
        label: 'DELETE QUESTION',
        callback: () => {
          if (questionToRemoveParam !== null) {
            openQuestionRequestModal(questionToRemoveParam);
          }
        },
        isActive: true,
      },
    ],
    [openQuestionRequestModal]
  );

  const actions = useMemo<Action[]>(
    () => [
      {
        label: 'CANCEL',
        variant: 'outlined',
        disabled: false,
        callback: () => setIsOpenDeleteQuestionRequestModal(false),
      },
      {
        label: 'YES, DELETE IT',
        variant: 'contained',
        disabled: false,
        callback: () => {
          if (questionToRemove !== null) {
            removeQuestion(questionToRemove);
          }
        },
      },
    ],
    [questionToRemove, removeQuestion]
  );

  return (
    <>
      <List>
        {questions?.progress.map((question, i) => {
          const hasAnswers = Array.isArray(question.answers) && question.answers.length > 0;

          return (
            <div key={question.id}>
              <ListItem
                style={{
                  backgroundColor: !hasAnswers ? (theme?.palette?.grey?.[200] ?? '#e0e0e0') : 'transparent',
                }}>
                <Grid container spacing={0}>
                  <Grid item xs={11}>
                    <Typography
                      component="div"
                      style={{
                        fontWeight: 700,
                        color: theme.palette.black,
                        fontSize: '1rem',
                        display: 'inline',
                        marginRight: '0.5rem',
                      }}>
                      {`${i + 1} - ${question.name} `}
                    </Typography>
                    {hasAnswers && <ModalAnswerRow task={question} />}
                  </Grid>
                  {permissions.canEditSetup && (
                    <Grid item xs={1} style={{ textAlign: 'right' }}>
                      <ExtraActionsMenu
                        options={optionsForExtraActionsMenu(question)}
                        dotsColor={theme.palette.grey[500]}
                      />
                    </Grid>
                  )}
                </Grid>
              </ListItem>
              <Divider />
            </div>
          );
        })}
      </List>
      <ConfirmationDialog
        open={isOpenDeleteQuestionRequestModal}
        title="Do you think this question will not be necessary?"
        onClose={() => setIsOpenDeleteQuestionRequestModal(false)}
        actions={actions}>
        <span>
          This modification will affect the original request to the company user, eliminating the question from its
          portal.
        </span>
      </ConfirmationDialog>
    </>
  );
};

export default CurrentRequestedQuestionsList;
